import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { useInView } from 'react-intersection-observer';

import { amplitudeService, ScreenName } from '../../../../../shared/amplitude';
import { SelectSortType } from '../../../../../shared/amplitude/events/review';
import { useScreenName } from '../../../../../shared/hooks';
import { useAppSelector } from '../../../../../shared/store';
import ScreenOut from '../../../../../shared/components/Pagination/ScreenOut';
import Loading from '../../../../../shared/components/Loading/Loading';
import ExclamationMark from '../../../../../shared/icons/ExclamationMark';

import { useReviewList } from '../../hooks';
import ReviewRow from '../ReviewRow';
import ReviewNoticeList from '../ReviewNotice/ReviewNoticeList';
import {
  EmptyFallback,
  EmptyText,
  FallbackView,
  ListHeader,
  ListSectionTop,
  NextButton,
  Pagination,
  PreviousButton,
  ReviewableButton,
  SortButton,
  SortButtonWrapper,
  TotalCount,
  View,
} from './styled-components';

type SortType = 'RECOMMEND' | 'RECENTLY';

interface Props {
  contentsProductNo: number;
  count: number;
}

export default function ReviewList({ contentsProductNo, count }: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortType, setSortType] = useState<SortType>('RECOMMEND');
  const ref = useRef<HTMLDivElement>(null);

  const router = useRouter();
  const { ref: view, inView } = useInView();
  const { data, status, fetchNextPage, hasNextPage, isFetchingNextPage } = useReviewList(
    contentsProductNo,
    { sortType },
    currentPage,
  );
  const {
    paramCode,
    pagination: { totalProductsCount },
  } = useAppSelector(({ productList }) => productList);

  const isEmptyList = !!data && data.reviews.length === 0;
  const disabledPrevious = currentPage === 1;
  const disabledNext = isFetchingNextPage || (!hasNextPage && !data?.pagination.nextPageParam);

  const logSortEvent = (currentSortType: SortType) => {
    try {
      const { browseEventName } = amplitudeService.syncBucketInstance();
      const payload = {
        browseEventName,
        sortType: currentSortType,
        searchCount: totalProductsCount,
        searchKeyword: paramCode,
      };

      amplitudeService.syncSetScreenName(ScreenName.PRODUCT_REVIEW_SUBTAB);
      amplitudeService.syncLogEvent(new SelectSortType(payload));
    } catch {}
  };

  function handleClickPrevious() {
    if (disabledPrevious) {
      return;
    }

    setCurrentPage(Math.max(currentPage - 1, 1));
  }

  function handleClickNext() {
    if (!hasNextPage) {
      return;
    }

    fetchNextPage().then(() => setCurrentPage(currentPage + 1));
  }

  function handleSortLike() {
    logSortEvent('RECOMMEND');
    setSortType('RECOMMEND');
  }

  function handleSortRecently() {
    logSortEvent('RECENTLY');
    setSortType('RECENTLY');
  }

  useScreenName(ScreenName.PRODUCT_DETAIL_DESCRIPTION);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortType]);

  useEffect(() => {
    if (ref?.current && inView) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage]);

  return (
    <View ref={view} hasReviewList={!isEmptyList}>
      <ListSectionTop ref={ref} />
      <ListHeader>
        {count > 0 ? <TotalCount>총 {count.toLocaleString()}개</TotalCount> : null}
        {!isEmptyList ? (
          <SortButtonWrapper>
            <SortButton isSelected={sortType === 'RECOMMEND'} onClick={handleSortLike}>
              추천순
            </SortButton>
            <SortButton isSelected={sortType === 'RECENTLY'} onClick={handleSortRecently}>
              최근등록순
            </SortButton>
          </SortButtonWrapper>
        ) : null}
      </ListHeader>
      {status === 'loading' ? (
        <FallbackView>
          <Loading isAbsolute={true} />
        </FallbackView>
      ) : null}
      <ReviewNoticeList />
      {status === 'success' && (
        <>
          {isEmptyList ? (
            <EmptyFallback>
              <ExclamationMark />
              <EmptyText>따끈한 첫 후기를 기다리고 있어요.</EmptyText>
              <ReviewableButton onClick={() => router.push('/mypage/review')}>작성 가능 후기</ReviewableButton>
            </EmptyFallback>
          ) : (
            data?.reviews.map((review) => <ReviewRow key={review.id} sortType={sortType} {...review} />)
          )}
        </>
      )}
      {!isEmptyList && (
        <Pagination>
          <PreviousButton disabled={disabledPrevious} onClick={handleClickPrevious}>
            <ScreenOut>이전</ScreenOut>
          </PreviousButton>
          <NextButton disabled={disabledNext} onClick={handleClickNext}>
            <ScreenOut>
              <ScreenOut>다음</ScreenOut>
            </ScreenOut>
          </NextButton>
        </Pagination>
      )}
    </View>
  );
}
